/*
 * WAWTech landing (layout/waw.liquid).
 *
 * Request form contract: the form section renders a wrapper with id="waw-request" and marks
 * its fields with data-waw-field="message" / data-waw-field="format". "Request" buttons scroll
 * to it and fill those fields; the same data is also sent as a "waw:request" event on document.
 */

const WAW_FORM_ID = 'waw-request';

function wawTrack(event, data = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...data });
}

function wawFillField(form, name, value) {
  const field = form.querySelector(`[data-waw-field="${name}"]`);
  if (!field || !value) return;

  if (field.tagName === 'SELECT') {
    const option = Array.from(field.options).find((item) => item.value === value || item.text === value);
    if (!option) return;
    field.value = option.value;
  } else {
    field.value = value;
  }

  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

function wawRequest(button) {
  const { wawModel: model, wawFormat: format } = button.dataset;
  const form = document.getElementById(WAW_FORM_ID);

  wawTrack('waw_request_click', { waw_model: model || '', waw_format: format || '' });
  document.dispatchEvent(new CustomEvent('waw:request', { detail: { model, format } }));

  if (!form) return;

  wawFillField(form, 'message', model);
  wawFillField(form, 'format', format);
  form.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function wawCopy(button) {
  const url = button.dataset.wawUrl || window.location.href;

  try {
    await navigator.clipboard.writeText(url);
  } catch (error) {
    const input = document.createElement('textarea');
    input.value = url;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    input.remove();
  }

  wawTrack('waw_copy_link');

  const label = button.querySelector('span');
  if (!label || !button.dataset.wawCopied) return;

  clearTimeout(button.wawCopyTimer);
  button.wawDefaultLabel = button.wawDefaultLabel || label.textContent;
  label.textContent = button.dataset.wawCopied;
  button.wawCopyTimer = setTimeout(() => {
    label.textContent = button.wawDefaultLabel;
  }, 2000);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-waw-action]');
  if (!button) return;

  switch (button.dataset.wawAction) {
    case 'request':
      event.preventDefault();
      wawRequest(button);
      break;
    case 'forward':
      wawTrack('waw_forward_click');
      break;
    case 'copy':
      event.preventDefault();
      wawCopy(button);
      break;
  }
});

class WawCatalog extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onClick(event) {
    switch (event.target.dataset.action) {
      case 'tab':
        this.select(event.target);
        break;
    }
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(event.target);
    if (index === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next);
    next.focus();
  }

  select(activeTab) {
    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', isActive);
      tab.tabIndex = isActive ? 0 : -1;

      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !isActive;
    });

    activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }
}

class WawStickyCta extends HTMLElement {
  connectedCallback() {
    this.resizeObserver = new ResizeObserver(() => this.updateOffset());
    this.resizeObserver.observe(this);

    const form = document.getElementById(WAW_FORM_ID);
    if (form) {
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-hidden', entry.isIntersecting);
      });
      this.intersectionObserver.observe(form);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.body.style.removeProperty('--waw-sticky-bar-height');
  }

  updateOffset() {
    document.body.style.setProperty('--waw-sticky-bar-height', `${this.offsetHeight}px`);
  }
}

// Posts the request to a Google Apps Script web app (data-endpoint) that writes it to a Google Sheet.
class WawRequest extends HTMLElement {
  static MIN_FILL_TIME = 3000;
  static UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  connectedCallback() {
    this.form = this.querySelector('[data-form]');
    this.error = this.querySelector('[data-error]');
    this.success = this.querySelector('[data-success]');
    this.startedAt = Date.now();

    this.localizeCountries();
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  // all_country_option_tags labels follow the store locale; the landing is English, values are English names.
  localizeCountries() {
    const select = this.form.elements.country;
    if (!select) return;

    // Drop the "---" separator and the duplicated shipping countries Shopify puts on top.
    const seen = new Set();
    const options = Array.from(select.options).filter((option) => {
      if (!option.value) return false;
      if (option.value === '---' || seen.has(option.value)) {
        option.remove();
        return false;
      }
      seen.add(option.value);
      return true;
    });
    options.forEach((option) => {
      option.textContent = option.value;
    });
    options.sort((a, b) => a.value.localeCompare(b.value, 'en')).forEach((option) => select.appendChild(option));
  }

  buildPayload() {
    const payload = new URLSearchParams(new FormData(this.form));
    const params = new URLSearchParams(window.location.search);

    WawRequest.UTM_KEYS.forEach((key) => payload.set(key, params.get(key) || ''));
    payload.set('page', window.location.origin + window.location.pathname);

    return payload;
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.classList.contains('loading')) return;

    this.error.hidden = true;

    // Bots fill the honeypot or submit instantly: pretend success, send nothing.
    if (this.form.elements.website.value || Date.now() - this.startedAt < WawRequest.MIN_FILL_TIME) {
      this.showSuccess();
      return;
    }

    this.classList.add('loading');
    this.form.setAttribute('aria-busy', 'true');

    try {
      if (!this.dataset.endpoint) throw new Error('Endpoint URL is not set in the section settings');

      const response = await fetch(this.dataset.endpoint, { method: 'POST', body: this.buildPayload() });
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Request failed');

      wawTrack('waw_request_submit', {
        waw_role: this.form.elements.role.value,
        waw_quantity: this.form.elements.quantity.value,
        waw_format: this.form.elements.format.value,
      });
      this.showSuccess();
    } catch (error) {
      console.error('[waw-request]', error);
      this.error.hidden = false;
    } finally {
      this.classList.remove('loading');
      this.form.removeAttribute('aria-busy');
    }
  }

  showSuccess() {
    this.form.hidden = true;
    this.success.hidden = false;
    this.success.focus({ preventScroll: true });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (document.querySelector('waw-catalog')) customElements.define('waw-catalog', WawCatalog);
if (document.querySelector('waw-request')) customElements.define('waw-request', WawRequest);
if (document.querySelector('waw-sticky-cta')) customElements.define('waw-sticky-cta', WawStickyCta);
